import React from "react";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import { ROUTES } from "../../routes/routerConfig";

export interface SupportCategory {
  title: string;
  subtitle: string;
  icon: React.ElementType;
  // Each line carries its own structure marker so this stays readable as copy — see the
  // convention below. SupportCategoriesGrid's guide modal renders them accordingly.
  guide: string[];
}

// Guide line markers (parsed by SupportCategoriesGrid):
//   "## "  section heading
//   "### " sub-heading under a section
//   "• "   bullet
//   "o "   nested bullet
//   anything else — a paragraph
//   "**…**" bold within any line, as the source document emphasises its lead-ins
//   "[text](url)" a link — opens in a new tab (see renderInline in SupportCategoriesGrid)
//
// Shared between HelpCenter (pre-login) and ContactUsHub (Raise Ticket / View Tickets),
// which both show the same "Common Support Categories" section per the Figma design.
// Every "raise a Service Request" link points at login: these guides are read pre-login on the
// public Help Center, and raising a request needs an authenticated session, so login is the
// honest first step rather than a page that would just bounce the visitor back.
//
// Kept relative on purpose. A relative path already follows whatever host the app is served
// from — localhost, SIT, UAT or production — so there is no environment variable to keep in
// sync and no way for one environment to link into another. REACT_APP_API_URL, used here
// before, is the API host (api-customerportaldiy-*.avanse.com), which is not a page a customer
// should ever be sent to.
const LOGIN_URL = ROUTES.Login.index;
export const SUPPORT_CATEGORIES: SupportCategory[] = [
  
  {
    title: "Foreclosure",
    subtitle:
      "Rules, eligibility, and step-by-step guidance for paying off your remaining loan outstanding in full to close your loan before its scheduled tenure.",
    icon: LockOutlinedIcon,
    guide: [
      "## What is Foreclosure?",
      "Foreclosure means paying off your remaining loan outstanding in full before the end of your tenure.",
      "Your final payoff amount includes:",
      "• Outstanding Principal: The remaining balance on your loan",
      "• Accrued Interest: Interest calculated up to the exact day of closure",
      "• Pending Dues: Any overdue EMIs or late fees (if applicable)",
      "• Applicable Foreclosure Charges: Fees based on your loan terms and type",
      "Applicable foreclosure charges depend on your loan type (View the Schedule of charges by clicking [here](https://www.avanse.com/schedule-of-charges))",
      "## How to foreclose your loan?",
      `• Request a Foreclosure Letter: Click [here](${LOGIN_URL}) to raise a Service Request to generate your foreclosure letter.`,
      "• Review the Payable Amount: The letter will detail your final settlement amount and the payment validity date.",
      "• Complete the Payment: Pay the full amount online before the validity date to ensure the quote remains accurate.",
      "• Save the Receipt: Keep your transaction confirmation or reference ID for your records.",
      "## Important note on validity:",
      "Interest accrues daily. If your Foreclosure Letter expires before you complete payment, please request an updated letter before paying.",
    ],
  },
  {
    title: "Understanding & Managing Your EMI",
    subtitle: "Quick guides for effortless, on-time monthly EMI payments.",
    icon: AccessTimeOutlinedIcon,
    guide: [
      "## What to remember before your upcoming EMI?",
      "• **Maintain Sufficient Balance:** Keep adequate funds in your registered bank account before your EMI due date to ensure smooth auto-debit.",
      "• **Avoid Double Payments:** If auto-debit (NACH/e-NACH) is active, do not make a manual payment unless explicitly requested by Avanse Financial Services. This prevents duplicate deductions.",
      "## How to check your EMI status?",
      "• Log in to the Avanse Financial Services Customer Portal or Mobile App.",
      "• Download your Statement of Account to confirm if your EMI payment has been credited to your loan account.",
      "• Processing Note: Auto-debit payments (NACH/e-NACH) typically take 2–3 working days to reflect in your Statement of Account.",
      "## Making a manual payment?",
      "• Complete your manual transfer at least 3 working days before your due date. This allows sufficient time for bank processing and ensures your loan balance updates before auto-debit triggers.",
      "• Avoiding Double Deduction: If paid too close to the due date, your scheduled auto-debit may still process.",
      "## What happens if the EMI payment timeline is missed or delayed?",
      "A failed or delayed EMI can lead to additional costs and impact your credit standing. Specifically, it may result in:",
      "• Overdue Balance: An active unpaid amount added to your loan account",
      "• Bounce & Late Fees: Penalty charges applied according to your loan agreement.",
      "• Credit Score Impact: Adverse reporting to credit bureaus",
      "Applicable charges depend on your loan type & terms (View the Schedule of charges by clicking [here](https://www.avanse.com/schedule-of-charges))",
      "## Need to change your repayment bank account?",
      `• **Submit Your Request:** Click [here](${LOGIN_URL}) to raise a Service Request to update your bank mandate.`,
      "• Keep Existing Account Funded: Continue maintaining required funds in your currently registered account until you receive confirmation that your new mandate is active.",
      "**Important Note:** Mandate approval typically takes 30 days. If an EMI is due during this transition, it will still be debited from your current bank account to avoid bounce charges.",
    ],
  },
  {
    title: "Statements & Loan Documents",
    subtitle:
      "Access your official loan documents anytime through the Avanse Financial Services Customer Portal & Mobile App.",
    icon: DescriptionOutlinedIcon,
    guide: [
      "## What documents can you access?",
      "• Statement of Account (SOA): Track credited payments, remaining balance, and full transaction history.",
      "• Repayment Schedule: View your monthly EMI breakdown, repayment dates, and remaining tenure.",
      "• Interest Certificate: Download your interest summary for the applicable period for tax filings and deductions.",
      "• No Objection Certificate (NOC): Official account closure letter once your loan is fully repaid (if applicable)",
      "## How to download your documents?",
      "• Log in to the Avanse Financial Services Customer Portal or Mobile App.",
      "• Select your loan account from the dashboard.",
      "• Go to Statements & Documents.",
      "• View or download the required document.",
      "## Can't find the document you need?",
      `Please raise a Service Request [here](${LOGIN_URL}).`,
    ],
  },
  {
    title: "Fund Release Request Essentials (Study Abroad Education Loan)",
    subtitle: "Quick guidelines to request and track your study abroad loan disbursements.",
    icon: SendOutlinedIcon,
    guide: [
      "## How to submit a fund release request?",
      "• Log In: Sign in to Avanse Financial Services Customer Portal using your Customer ID, registered email, or mobile number.",
      "• Initiate the Request: Navigate to Fund Release Request and select New Request.",
      "• Select Request Type: Choose one of the following:",
      "o Tranche Disbursement",
      "o Lien Removal",
      "o Reimbursement",
      "• Fill In & Upload: Enter the required details and upload your supporting documents.",
      "• Submit: Review your information and submit the details",
      "**Track Your Status:** Once submitted, you will receive a unique service request number to track this further. Please note that final approval and the disbursement amount are subject to document verification.",
      "## Document Checklist: What to keep ready before you start",
      "Requirements vary based on your request type. To ensure fast processing, please upload clear, legible copies of:",
      "• Fee & University Proof: University demand letter, fee-due screenshot, or official fee receipt/invoice",
      "• Payment & Disbursement Records: TT copy/proof of remittance",
      "• Student Details & Verification: University confirmation email, valid student visa copy, and updated overseas phone number/address.",
      "• Pending Compliance: Any pending post-disbursement documents from prior tranches.",
      "Important Note: Uploading incomplete or blurry documents will delay processing of your request.",
    ],
  },
  {
    title: "NACH Mandate",
    subtitle:
      "Never miss a due date. Set up auto-debit from your registered bank account for hassle-free repayments.",
    icon: HowToRegOutlinedIcon,
    guide: [
      "## What is a NACH Mandate?",
      "A NACH mandate is a simple and secure authorisation that allows Avanse to automatically collect your monthly EMI from your registered bank account on the due date. Once activated, you do not need to make the payment manually each month.",
      "## Things to Remember Before Your EMI Due Date",
      "• Maintain Adequate Funds: Ensure your registered bank account has a sufficient balance to cover the EMI.",
      "• Keep Account Active: Keep your bank account active and accessible until the debit is successfully processed.",
      "• Avoid Double Payments: If auto-debit (NACH/e-NACH) is active, refrain from making a manual payment unless specifically instructed by Avanse. This prevents duplicate payments.",
      "## Need to Change Your Registered Bank Account?",
      "Easily update your auto-debit (NACH) details by initiating a request online:",
      `• **Submit Request:** Click [here](${LOGIN_URL}) to Raise a Service Request to swap your bank details.`,
      "• Provide Verification Documents: If you're switching banks, you may need to upload updated bank statements, cancelled cheques, or account proof.",
      "• Verification & Activation: Once approved, your new account will automatically replace the old one for future EMIs.",
      "**Important Note:** Please keep sufficient funds in your current account until your new mandate is confirmed active. Account transfers typically take 1 to 2 months to complete.",
    ],
  },
  {
    title: "Loan Overview & Account Details",
    subtitle: "Access your key loan account information instantly anytime.",
    icon: AccountBalanceOutlinedIcon,
    guide: [
      "## Get Your Complete Loan Overview",
      "Track essential account details, schedules, and balances in one place:",
      "• Loan Summary: Loan Account Number, status, sanctioned amount, disbursed amount & more.",
      "• Repayment Details: Next EMI due date, payment amount, loan tenure, remaining tenure",
      "• Outstanding Balance: Real-time principal outstanding, accrued interest, and total balance",
      "• Interest Info: Current interest rate and rate type",
      "• Account Holders: Primary borrower and co-borrower details",
      "• Disbursement History: Latest disbursement amount, release date, beneficiary, and bank account details",
      "## How to check your Loan details?",
      "• Log In: Sign in to the Avanse Financial Services Customer Portal or Mobile App.",
      "• Select Account: Choose your specific Loan Account Number (LAN).",
      "• View Summary: Check your dashboard for a quick overview of balances and due dates.",
      "• Get Full Details: Go to Quick Help for comprehensive account information.",
      "**Managing Multiple Loans?** Make sure you select the correct LAN before reviewing your details.",
      "## Need more help with your loan account?",
      "Have questions or need clarification? **Raise a Service Request**, and our team will assist you further.",
    ],
  },
  {
    title: "Prepayment",
    subtitle:
      "Rules, eligibility, and step-by-step guidance for paying extra towards your loan principal",
    icon: CreditCardOutlinedIcon,
    guide: [
      "## What is prepayment?",
      "Paying extra beyond your monthly EMI to directly reduce your outstanding principal. A prepayment lowers your overall balance, reducing either your monthly EMI or your remaining tenure.",
      "## Prepayment Eligibility by Loan Type",
      "• Education Loan (Student Loan International & Education Loan Domestic): Prepay anytime with zero restrictions.",
      "• Educational Institution Loan: Allowed up to 2 times per year (product-specific limits and charges may apply)",
      "Applicable charges may vary as per your loan type, interest rate, amount, and tenure. View Schedule of Charges by clicking [here](https://www.avanse.com/schedule-of-charges).",
      "## How to make a Prepayment?",
      "### Education Loan (International & Domestic):",
      "• Log in to the Avanse Financial Services Customer Portal or Mobile App.",
      "• Go to Payments > Prepayment",
      "• Enter the amount and complete the payment using your preferred payment mode.",
      "• Save the transaction details for your records.",
      "### If you are making payments from outside India, please note:",
      "• Android App & Customer Portal: Accessible globally",
      "• iOS App: Available only within India. If you are abroad using an iPhone, please use the Customer Portal to make your payment.",
      "### Educational Institution Loans:",
      `• Raise a Service Request by clicking [here](${LOGIN_URL}) to get started.`,
    ],
  },
];
